import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData, postData, updateData, deleteData, VerifyData, ConvertEnquiryToSale } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, useMediaQuery, CircularProgress
} from '@mui/material';
import {
  Search, Add, Edit, Visibility, Delete, Refresh, FilterList, Assignment, CheckCircleOutline
} from '@mui/icons-material';
import EnquiryModal from './EnquiryModal';
import Pagination from '../../common/Pagination';

const EnquiryIndex = () => {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, userId, roleName } = useAuth();
  const { logAudit } = useAuditLog();
  const isMobile = useMediaQuery('(max-width:768px)');

  // Modal states (desktop)
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [formData, setFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});
  const [enquiryDetails, setEnquiryDetails] = useState([]);
  const [items, setItems] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getCurrentDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Loading & messages
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Confirm Order
  const [confirmOrderOpen, setConfirmOrderOpen] = useState(false);
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const [confirmOrderNo, setConfirmOrderNo] = useState('');
  const [confirmOrderLoading, setConfirmOrderLoading] = useState(false);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch
  const isExecutiveRole = (role) => {
    if (!role) return false;
    const normalized = role.toLowerCase().replace(/\s+/g, '');
    return normalized === 'salesexecutive' || normalized === 'executive';
  };

  const fetchEnquiries = async () => {
    try {
      const userIdParam = isExecutiveRole(roleName) ? userId : 0;
      console.log('[EnquiryIndex] roleName:', roleName, '| userIdParam:', userIdParam);
      const endpoint = `/${companyId}/${branchId}/${userIdParam}/${fromDate}/${toDate}`;
      const response = await fetchData('EnquiryApi', endpoint, token);
      let data = [];
      if (response) {
        if (response.response_data) data = Array.isArray(response.response_data) ? response.response_data : [];
        else if (Array.isArray(response)) data = response;
        else if (response.data) data = Array.isArray(response.data) ? response.data : [];
      }
      setEnquiries([...data]);
    } catch (e) {
      console.error('Error fetching enquiries:', e);
      setEnquiries([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (e) { setItems([]); }
  };

  const fetchBrands = async () => {
    try {
      const response = await fetchData('BrandApi', `/${companyId}`, token);
      setBrands(response.response_data || response || []);
    } catch (e) { setBrands([]); }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchData('CategoryApi', `/${companyId}`, token);
      setCategories(response.response_data || response || []);
    } catch (e) { setCategories([]); }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching enquiries...');
    await Promise.allSettled([fetchEnquiries(), fetchItems(), fetchBrands(), fetchCategories()]);
    setLoading(false);
  };

  const handleRefresh = () => fetchAllData();

  // Smart handlers: Mobile → full page, Desktop → modal
  const handleCreate = () => {
    if (isMobile) { history.push('/EnquiryForm/create'); }
    else { openCreateModal(); }
  };
  const handleView = (id) => {
    if (isMobile) { history.push(`/EnquiryForm/view/${id}`); }
    else { openEditViewModal('view', id); }
  };
  const handleEdit = (id) => {
    if (isMobile) { history.push(`/EnquiryForm/edit/${id}`); }
    else { openEditViewModal('edit', id); }
  };

  // Desktop modal helpers
  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('EnquiryApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      let enquiryNo = '';
      if (billPrefix && finYear) enquiryNo = `${billPrefix}/${billNumber}/${finYear}`;
      else if (finYear) enquiryNo = `${billNumber}/${finYear}`;
      else if (billPrefix) enquiryNo = `${billPrefix}/${billNumber}`;
      else enquiryNo = `${billNumber}`;
      return { billNumber, billPrefix, finYear, enquiryNo };
    } catch (e) { return { billNumber: 1, billPrefix: '', finYear: '', enquiryNo: '1' }; }
  };

  const openCreateModal = async () => {
    setLoading(true);
    const maxBillData = await fetchMaxBillNo();
    setFormData({
      CompanyId: companyId, BranchId: branchId || 1, MasterId: null,
      EnquiryNumber: maxBillData.billNumber, EnquiryNo: maxBillData.enquiryNo,
      EnquiryPrefix: maxBillData.billPrefix, FinYear: maxBillData.finYear,
      EnquiryDate: new Date().toISOString().split('T')[0],
      CustomerName: '', ContactNumber: '', Address: '',
      TotalQuantity: 0, TotalAmount: 0, IsActive: true, IsSaled: false
    });
    setEnquiryDetails([]);
    setModalAction('create');
    setOpenModal(true);
    setLoading(false);
  };

  const getMopLabel = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'number') {
      const map = { 1: 'MOP1', 2: 'MOP2', 3: 'MOP3', 4: 'MOP4', 5: 'MOP5', 6: 'MRP', 7: 'Sale Rate' };
      return map[val] || String(val);
    }
    return String(val);
  };

  const openEditViewModal = async (action, id) => {
    setLoading(true);
    try {
      const response = await fetchData('EnquiryApi', `/${id}`, token);
      const freshData = response.response_data || response;
      const enquiryData = freshData.Enquiry || freshData;
      const details = freshData.EnquiryDetails || freshData.Details || [];
      const fd = {
        Id: enquiryData.Id, CompanyId: enquiryData.CompanyId || companyId,
        BranchId: enquiryData.BranchId || branchId, MasterId: enquiryData.MasterId || null,
        EnquiryNumber: enquiryData.EnquiryNumber || 0, EnquiryNo: enquiryData.EnquiryNo || '',
        EnquiryPrefix: enquiryData.EnquiryPrefix || '', FinYear: enquiryData.FinYear || '',
        EnquiryDate: enquiryData.EnquiryDate?.split('T')[0] || '',
        CustomerName: enquiryData.CustomerName || '', ContactNumber: enquiryData.ContactNumber || '',
        Address: enquiryData.Address || '', TotalQuantity: enquiryData.TotalQuantity || 0,
        TotalAmount: enquiryData.TotalAmount || 0, IsActive: enquiryData.IsActive ?? true,
        IsSaled: enquiryData.IsSaled ?? false,
        IsConfirmOrder: enquiryData.IsConfirmOrder ?? false,
        CreatedBy: enquiryData.CreatedBy || null, CreatedDate: enquiryData.CreatedDate || null
      };
      setFormData(fd);
      setOriginalFormData(fd);
      setEnquiryDetails(details.map(d => ({
        Id: d.Id || 0, ItemId: d.ItemId, HSNCode: d.HSNCode || '', GSTRate: d.GSTRate || 0,
        Quantity: d.Quantity || 0, Rate: d.Rate || 0, Amount: d.Amount || 0,
        DiscountPercentage: d.DiscountPercentage || 0, DiscountAmount: d.DiscountAmount || 0,
        TaxableAmount: d.TaxableAmount || 0, CGSTAmount: d.CGSTAmount || 0,
        SGSTAmount: d.SGSTAmount || 0, IGSTAmount: d.IGSTAmount || 0,
        TotalAmount: d.TotalAmount || 0, //WhichMOP: d.WhichMOP || null,
        IsActive: d.IsActive ?? true, WhichMOP: getMopLabel(d.WhichMOP),
        StarRating: d.StarRating || 0,
        CreatedBy: d.CreatedBy || null, CreatedDate: d.CreatedDate || null
      })));
      setModalAction(action);
      if (action === 'view') {
        logAudit(AUDIT_ACTION.VIEW, 'EnquiryApi', enquiryData.Id || id || 0);
      }
      setOpenModal(true);
    } catch (e) { setError('Error loading enquiry'); }
    setLoading(false);
  };

  const handleCloseModal = () => { setOpenModal(false); setFormData({}); setEnquiryDetails([]); };

  // Save (desktop modal)
  const handleSave = async () => {
    if (!formData.CustomerName?.trim()) { setError('Customer Name is required'); return; }
    if (!formData.ContactNumber?.trim()) { setError('Contact Number is required'); return; }
    if (enquiryDetails.length === 0) { setError('Please add at least one item'); return; }
    if (enquiryDetails.some(d => !d.ItemId || d.Quantity <= 0)) { setError('Please fill all item details'); return; }
    setLoading(true);
    try {
      const whichMopToInt = (val) => {
        if (val === null || val === undefined || val === '') return null;
        if (typeof val === 'number') return val;
        const map = { 'MOP1': 1, 'MOP2': 2, 'MOP3': 3, 'MOP4': 4, 'MOP5': 5, 'MRP': 6, 'Sale Rate': 7 };
        return map[val] ?? null;
      };
      const enquiryVM = {
        Enquiry: {
          ...formData, CompanyId: companyId, BranchId: branchId || 1,
          IsConfirmOrder: modalAction === 'create' ? false : (formData.IsConfirmOrder ?? false),
          TotalQuantity: enquiryDetails.reduce((s, d) => s + (parseFloat(d.Quantity) || 0), 0),
          TotalAmount: enquiryDetails.reduce((s, d) => s + (parseFloat(d.TotalAmount) || 0), 0),
          CreatedBy: modalAction === 'create' ? userId : (formData.CreatedBy || userId), ModifiedBy: userId,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : (formData.CreatedDate || new Date().toISOString()),
          ModifiedDate: new Date().toISOString()
        },
        EnquiryDetails: enquiryDetails.map(d => ({
          Id: d.Id || 0,
          EnquiryId: formData.Id || 0,
          ItemId: d.ItemId,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          Quantity: parseFloat(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscountPercentage: parseFloat(d.DiscountPercentage) || 0,
          DiscountAmount: parseFloat(d.DiscountAmount) || 0,
          TaxableAmount: parseFloat(d.TaxableAmount) || 0,
          CGSTAmount: parseFloat(d.CGSTAmount) || 0,
          SGSTAmount: parseFloat(d.SGSTAmount) || 0,
          IGSTAmount: parseFloat(d.IGSTAmount) || 0,
          TotalAmount: parseFloat(d.TotalAmount) || 0,
          WhichMOP: whichMopToInt(d.WhichMOP),
          StarRating: d.StarRating || 0,
          IsActive: d.IsActive ?? true,
          CompanyId: companyId, BranchId: branchId || 1,
          CreatedBy: modalAction === 'create' ? userId : (d.CreatedBy || userId),
          ModifiedBy: userId,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : (d.CreatedDate || new Date().toISOString()),
          ModifiedDate: new Date().toISOString()
        }))
      };
      if (modalAction === 'create') { await postData('EnquiryApi', '', enquiryVM, token); setSuccess('Enquiry created!'); logAudit(AUDIT_ACTION.CREATE, 'EnquiryApi', 0, null, { CustomerName: formData.CustomerName || '' }); }
      else { await updateData('EnquiryApi', formData.Id, enquiryVM, token); setSuccess('Enquiry updated!'); logAudit(AUDIT_ACTION.UPDATE, 'EnquiryApi', formData.Id, { ...originalFormData }, { ...formData }); }
      handleCloseModal();
      fetchEnquiries();
    } catch (e) { setError(e.response?.data?.message || e.response?.data?.title || 'Error saving enquiry'); }
    finally { setLoading(false); }
  };

  // Delete
  const handleDeleteClick = (id, no) => { setDeleteItemId(id); setDeleteItemNo(no); setDeleteModalOpen(true); };
  const handleDeleteConfirm = async () => {
    setLoading(true);
    try {
      await deleteData('EnquiryApi', deleteItemId, token);
      setSuccess('Enquiry deleted!');
    logAudit(AUDIT_ACTION.DELETE, 'EnquiryApi', deleteItemId, { Id: deleteItemId }, null);
      setDeleteModalOpen(false);
      fetchEnquiries();
    } catch (e) { setError('Error deleting enquiry'); }
    finally { setLoading(false); }
  };

  // Confirm Order
  const handleConfirmOrderClick = (item) => {
    setConfirmOrderId(item.Id);
    setConfirmOrderNo(item.EnquiryNo || `${item.EnquiryPrefix || ''}/${item.EnquiryNumber || ''}`);
    setConfirmOrderOpen(true);
  };

  const handleConfirmOrderSubmit = async () => {
    setConfirmOrderLoading(true);
    try {
      await ConvertEnquiryToSale('EnquiryApi', confirmOrderId, token);
      setSuccess(`Enquiry ${confirmOrderNo} confirmed as order!`);
      logAudit(AUDIT_ACTION.APPROVE, 'EnquiryApi', confirmOrderId);
      setConfirmOrderOpen(false);
      fetchEnquiries();
    } catch (e) {
      setError('Error confirming order');
    } finally {
      setConfirmOrderLoading(false);
    }
  };

  // Filter & Pagination
  const filteredEnquiries = enquiries.filter(item => {
    const s = searchTerm.toLowerCase();
    return (item.EnquiryNo || '').toLowerCase().includes(s) ||
      (item.CustomerName || '').toLowerCase().includes(s) ||
      (item.ContactNumber || '').toLowerCase().includes(s) ||
      String(item.TotalAmount || '').includes(s);
  });
  const totalPages = Math.ceil(filteredEnquiries.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredEnquiries.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amt) => `₹${(parseFloat(amt) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  };

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading} message={loadingMessage} />

      {/* Snackbars */}
      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
      </Snackbar>

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Enquiry Management</h4>
        </div>

        {/* Search, Filters and Create */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="New Enquiry" arrow>
                <IconButton onClick={handleCreate} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                  <Add sx={{ fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Enquiry No, Customer..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Enquiry No, Customer..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <Tooltip title="Refresh" arrow>
              <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="New Enquiry" arrow>
              <Button variant="contained" onClick={handleCreate}
                sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>New Enquiry</span>
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Enquiry No.', 'Date', 'Customer', 'Contact', ...(!isExecutiveRole(roleName) ? ['Executive'] : []), 'Qty', 'Amount', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id} sx={{
                      background: item.IsConfirmOrder
                        ? (index % 2 === 0 ? '#f0fdf4' : '#dcfce7')
                        : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'),
                      borderBottom: item.IsConfirmOrder ? '2px solid #bbf7d0' : '2px solid #f0f0f0',
                      borderLeft: item.IsConfirmOrder ? '3px solid #16a34a' : '3px solid transparent',
                      '& .MuiTableCell-root': {
                        backgroundColor: 'inherit',
                      }
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Assignment sx={{ fontSize: 16, color: '#2C3E6E' }} />
                          {item.EnquiryNo || `${item.EnquiryPrefix || ''}/${item.EnquiryNumber || ''}`}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.EnquiryDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.CustomerName || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.ContactNumber || '-'}</TableCell>
                      {!isExecutiveRole(roleName) && (
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{item.ExecutiveName || item.UserName || item.CreatedByName || '-'}</TableCell>
                      )}
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600, textAlign: 'right' }}>{item.TotalQuantity || 0}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.TotalAmount)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        {item.IsSaled
                          ? <Chip label="Sold" size="small" color="success" />
                          : item.IsConfirmOrder
                            ? <Chip label="Confirmed" size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 700, fontSize: '11px' }} />
                            : <Chip label="Pending" size="small" color="warning" />
                        }
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <Tooltip title="Confirm Order" arrow>
                            <IconButton size="small" onClick={() => handleConfirmOrderClick(item)} disabled={item.IsSaled || item.IsConfirmOrder}
                              sx={{ color: item.IsConfirmOrder ? '#059669' : '#d97706', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26 }}>
                              <CheckCircleOutline sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View" arrow>
                            <IconButton size="small" onClick={() => handleView(item.Id)} sx={{ color: '#2C3E6E', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26 }}>
                              <Visibility sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit" arrow>
                            <IconButton size="small" onClick={() => handleEdit(item.Id)} disabled={item.IsSaled} sx={{ color: '#059669', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26 }}>
                              <Edit sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" onClick={() => handleDeleteClick(item.Id, item.EnquiryNo)} disabled={item.IsSaled} sx={{ color: '#dc2626', border: '1px solid #e2e8f0', borderRadius: '6px', width: 26, height: 26, display: item.IsSaled ? 'none' : 'inline-flex' }}>
                              <Delete sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }}>
                      No enquiries found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        {filteredEnquiries.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={filteredEnquiries.length}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={(val) => { setRowsPerPage(val); setCurrentPage(1); }}
          />
        )}
      </div>

      {/* Confirm Order Modal */}
      <Dialog open={confirmOrderOpen} onClose={() => !confirmOrderLoading && setConfirmOrderOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #d97706 0%, #f59e0b 100%)', px: 2.5, py: 1.75 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline sx={{ color: '#fff', fontSize: 22 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>Confirm Order</Typography>
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          {/* <Typography variant="body2" sx={{ color: '#374151' }}>
            Do you want to Place This enquiry <strong style={{ color: '#1e40af' }}>{confirmOrderNo}</strong> as an Sale?
          </Typography> */}
          <Typography variant="body2" sx={{ color: '#374151' }}>
            Are you sure you want to convert the enquiry <strong style={{ color: '#1e40af' }}>{confirmOrderNo}</strong> into a sale?
          </Typography>
          <Typography variant="caption" sx={{ color: '#6b7280', mt: 1, display: 'block' }}>
            This will mark the enquiry as a Place order.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setConfirmOrderOpen(false)} variant="outlined" size="small"
            disabled={confirmOrderLoading} sx={{ textTransform: 'none', borderRadius: '8px' }}>
            No, Cancel
          </Button>
          <Button onClick={handleConfirmOrderSubmit} variant="contained" size="small"
            disabled={confirmOrderLoading}
            sx={{ textTransform: 'none', borderRadius: '8px', bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
            {confirmOrderLoading ? <CircularProgress size={14} sx={{ color: '#fff', mr: 1 }} /> : null}
            Yes, Place Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Modal */}
      {!isMobile && (
        <EnquiryModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          enquiryDetails={enquiryDetails}
          setEnquiryDetails={setEnquiryDetails}
          onSave={handleSave}
          items={items}
          brands={brands}
          categories={categories}
          loading={loading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Delete Confirmation */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#dc2626' }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete enquiry <strong>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteModalOpen(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error" size="small">Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EnquiryIndex;
